// React

// Packages

// Utilities
import { useInitiativeDataStore } from 'utilities/store';

// Hooks
import { useLabels, useTagging, useContext, useFormat } from 'utilities/hooks';

const useMetricHelpers = () => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { CONSTANTS } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label, tagLabel, getValueLabel } = useLabels();
    const { CONTEXTS, CONTEXT } = useContext();
    const tagging = useTagging({
        relationKey: 'Initiative_Activity_Success_Metric__c',
    });
    const format = useFormat();

    // ///////////////////
    // METHODS
    // ///////////////////

    function isAudience(item) {
        if (
            (item?.Priority__c &&
                item?.Type__c === CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE) ||
            item?.metricType === CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
        ) {
            return true;
        }
        return false;
    }

    function getPreTitle(item) {
        return isAudience(item)
            ? getValueLabel(
                  'Initiative_Activity_Success_Metric__c.Priority__c',
                  item?.Priority__c
              )
            : label('Metric_Additional');
    }

    function getTitle(item, report = null) {
        if (isAudience(item)) {
            // Report is due to metric focus - it's context based on funder just as reports
            if (CONTEXT === CONTEXTS.REPORT || report) {
                return (
                    tagLabel(tagging.getCurrentTags(item)[0]?.Tag__r) ||
                    tagLabel(item?.tags?.[0]?.tags?.[0]) ||
                    null
                    // Vi kan antage der kun er ét tag pr. audience metric i en report kontekst
                );
            } else {
                // If not report show all tags of all funders for metric
                return tagging
                    .getCurrentTags(item)
                    .map(t => [
                        tagLabel(t?.Tag__r),
                        tagging.hasOwnTags(item) ? t?.Account_Name__c : null,
                    ]);
            }
        } else {
            return item.Name;
        }
    }

    function getPostTitle(item) {
        // We have to rely on either data from salesforce or data from the creation process which is different.
        if (isAudience(item)) {
            const genderValue = Array.isArray(item?.Gender__c)
                ? item?.Gender__c[0]?.selectValue
                : item?.Gender__c;
            const genderOtherValue =
                genderValue ===
                CONSTANTS.ACTIVITY_SUCCESS_METRICS.INDICATOR_GENDER_OTHER
                    ? Array.isArray(item?.Gender__c)
                        ? item?.Gender__c[0]?.textValue
                        : item?.Gender_Other__c
                    : null;
            const gender = genderValue
                ? `${getValueLabel(
                      'Initiative_Activity_Success_Metric__c.Gender__c',
                      genderValue
                  )}${genderOtherValue ? ` (${genderOtherValue})` : ''} ${label(
                      'MetricGender'
                  )}`
                : null;
            return [
                gender,
                item?.Geographical_Reach__c
                    ? `${item?.Geographical_Reach__c} ${label(
                          'MetricGeoReach'
                      )}`
                    : null,
            ].filter(x => x);
        } else if (
            item.Type__c === CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL
        ) {
            return [item?.CurrencyIsoCode];
        }
    }

    function getTaggingItems(item) {
        if (isAudience(item)) {
            return [];
        }
        return tagging.getCurrentTags(item);
    }

    function hasTarget(item) {
        return (
            item?.Target__c &&
            item?.Target__c !== 0 &&
            item?.Target__c !== '' &&
            item?.Target__c !== null
        );
    }

    function getCurrentStatus(item) {
        return format.number(item?.Current_Status__c);
    }

    function getTarget(item) {
        return format.number(item?.Target__c);
    }

    // ///////////////////
    // RETURN
    // ///////////////////

    return {
        isAudience,
        getPreTitle,
        getTitle,
        getPostTitle,
        getTaggingItems,
        hasTarget,
        getCurrentStatus,
        getTarget,
    };
};

export default useMetricHelpers;
